import React, { useEffect, useRef, useState } from "react";
import { createChatSession, streamChatMessage, fetchChatConfig } from "./api";
import type { ChatMessage, ChatProps } from "./types";

const DEFAULT_PRIMARY = "#111827";

type ChatTextToken =
  | { kind: "text"; value: string }
  | { kind: "bold"; value: string }
  | { kind: "link"; href: string; label: string };

// Links are matched in their own pass BEFORE bold so a clickable link is never
// swallowed by a surrounding **bold** span (`**[Pay](url)**` must still render an
// <a>, not inert bold text). Markdown link [label](url) is the first alternative
// so its closing ")" is consumed as link syntax — otherwise the bare-URL branch's
// greedy [^\s]+ grabs the ")" into the href and the link 404s (the reported
// "Complete Payment" payment-link bug). The server-side WhatsApp equivalent lives
// in apps/api .../chat-agent/formatForWhatsApp.service.ts — keep them in sync.
const LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s]+)/g;
const BOLD_RE = /\*\*([^*]+)\*\*/g;

// Trim trailing sentence punctuation off a bare (auto-linkified) URL so
// "see https://x/pay." doesn't route to "…/pay.". A trailing ")"/"]" is only
// stripped when unmatched, leaving wiki-style "/Foo_(bar)" links intact.
function splitTrailingPunct(url: string): [string, string] {
  let end = url.length;
  while (end > 0) {
    const ch = url[end - 1];
    if (".,;:!?\"'".indexOf(ch) !== -1) {
      end--;
      continue;
    }
    if (ch === ")" && url.lastIndexOf("(", end - 1) === -1) {
      end--;
      continue;
    }
    if (ch === "]" && url.lastIndexOf("[", end - 1) === -1) {
      end--;
      continue;
    }
    break;
  }
  return [url.slice(0, end), url.slice(end)];
}

// Within a plain (non-link) text slice, split out **bold** runs into tokens.
function pushTextTokens(text: string, tokens: ChatTextToken[]): void {
  let last = 0;
  let m: RegExpExecArray | null;
  BOLD_RE.lastIndex = 0;
  while ((m = BOLD_RE.exec(text)) !== null) {
    if (m.index > last) {
      tokens.push({ kind: "text", value: text.slice(last, m.index) });
    }
    tokens.push({ kind: "bold", value: m[1] });
    last = BOLD_RE.lastIndex;
  }
  if (last < text.length) {
    tokens.push({ kind: "text", value: text.slice(last) });
  }
}

/**
 * Tokenize assistant/customer text into link / bold / plain-text spans.
 * Handles markdown links the server emits (`[label](url)`), bare URLs, and
 * **bold**. Links win over bold (see LINK_RE). Exported for unit testing.
 */
export function tokenizeChatText(text: string): ChatTextToken[] {
  const tokens: ChatTextToken[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > last) {
      pushTextTokens(text.slice(last, m.index), tokens);
    }
    if (m[1] !== undefined && m[2] !== undefined) {
      tokens.push({ kind: "link", href: m[2], label: m[1] });
    } else if (m[3] !== undefined) {
      const [url, trailing] = splitTrailingPunct(m[3]);
      tokens.push({ kind: "link", href: url, label: url });
      if (trailing) pushTextTokens(trailing, tokens);
    }
    last = LINK_RE.lastIndex;
  }
  if (last < text.length) {
    pushTextTokens(text.slice(last), tokens);
  }
  return tokens;
}

/**
 * Renders assistant/customer text into React nodes: markdown links
 * `[label](url)`, bare clickable URLs (payment links!) and **bold** segments.
 * Plain, dependency-free — no dangerouslySetHTML.
 */
function renderContent(text: string): React.ReactNode[] {
  return tokenizeChatText(text).map((tok, i) => {
    if (tok.kind === "link") {
      return (
        <a
          key={i}
          href={tok.href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline", wordBreak: "break-all" }}
        >
          {tok.label}
        </a>
      );
    }
    if (tok.kind === "bold") {
      return <strong key={i}>{tok.value}</strong>;
    }
    return <span key={i}>{tok.value}</span>;
  });
}

export function Chat({
  businessSlug,
  apiUrl,
  primaryColor,
  position = "bottom-right",
  title,
  channel = "webcomponent",
  bottomOffset = 20,
  zIndex = 2147483000,
  authToken
}: ChatProps) {
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [schoolName, setSchoolName] = useState<string>("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Server-side render decision (display toggle + hide-when-no-credits).
  // null = still loading (render nothing yet). Fails OPEN on fetch errors so
  // an API hiccup can't hide chat for every school — the in-chat
  // "unavailable" message is the backstop when credits actually run out.
  const [shouldRender, setShouldRender] = useState<boolean | null>(null);
  useEffect(() => {
    if (!businessSlug) return;
    let cancelled = false;
    // Bound the request so a hung/stalled connection (no response, no error)
    // can't leave the bubble stuck hidden forever — aborting trips the catch
    // below, which fails OPEN just like any other fetch failure.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    fetchChatConfig(businessSlug, channel, apiUrl, controller.signal)
      .then(cfg => {
        if (!cancelled) setShouldRender(cfg.render);
      })
      .catch(() => {
        if (!cancelled) setShouldRender(true);
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      cancelled = true;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [businessSlug, channel, apiUrl]);

  // When the embedder sets `primary-color` we use it; otherwise we follow the
  // school's web-component Theme (the `--primary` CSS variable injected into the
  // shadow root by the theme loader), falling back to a sensible default.
  const accent =
    primaryColor && primaryColor.trim()
      ? primaryColor
      : `var(--primary, ${DEFAULT_PRIMARY})`;
  const side = position === "bottom-left" ? { left: 20 } : { right: 20 };

  // Lazily start a session the first time the panel opens.
  useEffect(() => {
    if (!open || started || !businessSlug) return;
    setStarted(true);
    void (async () => {
      try {
        const session = await createChatSession(businessSlug, apiUrl);
        setSessionId(session.sessionId);
        setSchoolName(session.schoolName);
        setMessages([{ role: "assistant", content: session.welcomeMessage }]);
      } catch {
        setError("Sorry, the assistant is unavailable right now.");
      }
    })();
  }, [open, started, businessSlug, apiUrl]);

  // Keep the message list scrolled to the latest turn.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  function appendToLastAssistant(delta: string) {
    setMessages(prev => {
      const copy = prev.slice();
      const last = copy[copy.length - 1];
      if (last && last.role === "assistant") {
        copy[copy.length - 1] = { ...last, content: last.content + delta };
      }
      return copy;
    });
  }

  async function handleSend() {
    const text = input.trim();
    if (!text || sending || !sessionId || !businessSlug) return;
    setInput("");
    setError(null);
    // Add the user turn + an empty assistant bubble the stream fills in.
    setMessages(prev => [
      ...prev,
      { role: "user", content: text },
      { role: "assistant", content: "" }
    ]);
    setSending(true);
    try {
      await streamChatMessage(
        businessSlug,
        sessionId,
        text,
        apiUrl,
        delta => appendToLastAssistant(delta),
        authToken
      );
    } catch {
      setMessages(prev => {
        const copy = prev.slice();
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          copy[copy.length - 1] = {
            ...last,
            content: "Sorry, something went wrong. Please try again."
          };
        }
        return copy;
      });
    } finally {
      setSending(false);
    }
  }

  if (!businessSlug || shouldRender !== true) return null;

  const headerTitle = title || schoolName || "Chat with us";

  return (
    <div
      style={{
        position: "fixed",
        bottom: bottomOffset,
        ...side,
        zIndex,
        fontFamily:
          "Switzer, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
      }}
    >
      {open && (
        <div
          style={{
            position: "absolute",
            bottom: 72,
            ...(position === "bottom-left" ? { left: 0 } : { right: 0 }),
            width: "min(380px, calc(100vw - 40px))",
            // 100 = the panel's 72px lift above the launcher + 28px breathing
            // room at the top. Subtracting bottomOffset too keeps the header
            // on-screen when the bubble is raised above a bottom bar/badge.
            height: `min(560px, calc(100vh - ${bottomOffset + 100}px))`,
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.06)"
          }}
        >
          {/* Header */}
          <div
            style={{
              background: accent,
              color: "#fff",
              padding: "14px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 15 }}>{headerTitle}</span>
            <button
              aria-label="Close chat"
              onClick={() => setOpen(false)}
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                fontSize: 20,
                lineHeight: 1,
                cursor: "pointer",
                padding: 4
              }}
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              background: "#f9fafb"
            }}
          >
            {messages.map((m, i) => (
              <div
                key={i}
                style={{
                  alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                  maxWidth: "85%",
                  background: m.role === "user" ? accent : "#fff",
                  color: m.role === "user" ? "#fff" : "#111827",
                  border: m.role === "user" ? "none" : "1px solid #e5e7eb",
                  borderRadius: 14,
                  padding: "9px 12px",
                  fontSize: 14,
                  lineHeight: 1.45,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word"
                }}
              >
                {m.role === "assistant" && !m.content
                  ? <TypingDots />
                  : renderContent(m.content)}
              </div>
            ))}

            {error && (
              <div style={{ color: "#dc2626", fontSize: 13, textAlign: "center" }}>
                {error}
              </div>
            )}
          </div>

          {/* Input */}
          <div
            style={{
              borderTop: "1px solid #e5e7eb",
              padding: 10,
              display: "flex",
              gap: 8,
              alignItems: "flex-end",
              background: "#fff"
            }}
          >
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              placeholder="Type your message…"
              rows={1}
              disabled={!sessionId}
              style={{
                flex: 1,
                resize: "none",
                border: "1px solid #d1d5db",
                borderRadius: 10,
                padding: "9px 11px",
                fontSize: 14,
                fontFamily: "inherit",
                outline: "none",
                maxHeight: 120
              }}
            />
            <button
              onClick={() => void handleSend()}
              disabled={!input.trim() || sending || !sessionId}
              aria-label="Send message"
              style={{
                background: accent,
                color: "#fff",
                border: "none",
                borderRadius: 10,
                padding: "9px 14px",
                fontSize: 14,
                fontWeight: 600,
                cursor: !input.trim() || sending ? "default" : "pointer",
                opacity: !input.trim() || sending ? 0.6 : 1
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}

      {/* Floating bubble */}
      <button
        aria-label={open ? "Close chat" : "Open chat"}
        onClick={() => setOpen(o => !o)}
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: accent,
          color: "#fff",
          border: "none",
          boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {open ? (
          <span style={{ fontSize: 24, lineHeight: 1 }}>×</span>
        ) : (
          <ChatIcon />
        )}
      </button>
    </div>
  );
}

function ChatIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 0 1-.9-3.8 8.38 8.38 0 0 1 8.5-8.5 8.38 8.38 0 0 1 8.5 8.5z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TypingDots() {
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {[0, 1, 2].map(i => (
        <span
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#9ca3af",
            display: "inline-block",
            animation: "baluu-chat-blink 1.2s infinite",
            animationDelay: `${i * 0.2}s`
          }}
        />
      ))}
      <style>{`@keyframes baluu-chat-blink{0%,80%,100%{opacity:.3}40%{opacity:1}}`}</style>
    </span>
  );
}
